import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Loader2, Mail, MailOpen, Send, Inbox, ExternalLink, Users, RefreshCw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ESTADO_LABEL = {
  nuevo: 'Nuevo',
  en_gestion: 'En gestión',
  pendiente_externa: 'Pendiente',
  respondido: 'Respondido',
  resuelto: 'Resuelto',
};

/**
 * Historial de correos vinculados a un matriculado.
 * Muestra tabs (Todos / Recibidos / Enviados), lista compacta, y link a la bandeja.
 * Props:
 *  - trabajadorId (string, required)
 *  - initialLimit (default 20)
 */
export function HistorialCorreosMatriculado({ trabajadorId, initialLimit = 20 }) {
  const [carpeta, setCarpeta] = useState('todas');
  const [convs, setConvs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    if (!trabajadorId) return;
    let cancelado = false;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(
          `${API_URL}/bandeja-unificada/matriculados/${trabajadorId}/conversaciones-previas?carpeta=${carpeta}&limit=${initialLimit}`
        );
        if (!cancelado) setConvs(r.data || []);
      } catch (e) {
        console.error('[historial-correos]', e);
        if (!cancelado) setConvs([]);
      }
      if (!cancelado) setLoading(false);
    })();
    return () => { cancelado = true; };
  }, [trabajadorId, carpeta, initialLimit, refreshTick]);

  const stats = useMemo(() => {
    const total = convs.length;
    const salientes = convs.filter(c => c.es_saliente).length;
    const entrantes = total - salientes;
    return { total, salientes, entrantes };
  }, [convs]);

  const fmtFecha = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      return d.toLocaleString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
    } catch { return iso; }
  };

  const tabBtn = (v, label, Icon) => (
    <button
      onClick={() => setCarpeta(v)}
      className={`px-3 py-1.5 text-xs rounded-full inline-flex items-center gap-1 border ${carpeta === v ? 'bg-purple-100 border-purple-300 text-purple-800 font-medium' : 'hover:bg-gray-50 border-gray-200 text-gray-600'}`}
      data-testid={`historial-tab-${v}`}
    >
      <Icon className="w-3.5 h-3.5" /> {label}
    </button>
  );

  return (
    <div className="border rounded-lg bg-white" data-testid="historial-correos">
      <div className="border-b px-4 py-2.5 flex items-center justify-between gap-2 flex-wrap">
        <h3 className="text-sm font-semibold flex items-center gap-2">
          <Mail className="w-4 h-4 text-purple-600" /> Historial de correos
          <span className="text-xs font-normal text-gray-500">
            ({stats.total}{stats.total > 0 && ` · ${stats.entrantes} recibidos · ${stats.salientes} enviados`})
          </span>
        </h3>
        <div className="flex items-center gap-1.5">
          {tabBtn('todas', 'Todos', Inbox)}
          {tabBtn('entrada', 'Recibidos', MailOpen)}
          {tabBtn('enviados', 'Enviados', Send)}
          <button
            onClick={() => setRefreshTick(t => t + 1)}
            className="p-1.5 text-gray-500 hover:text-purple-600"
            title="Refrescar"
            data-testid="historial-refresh"
          >
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      <div className="max-h-72 overflow-y-auto">
        {loading ? (
          <div className="py-8 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-purple-600" /></div>
        ) : convs.length === 0 ? (
          <p className="py-8 text-center text-xs text-gray-500 italic" data-testid="historial-empty">
            No hay correos {carpeta === 'todas' ? '' : (carpeta === 'entrada' ? 'recibidos' : 'enviados')} vinculados a este matriculado.
          </p>
        ) : (
          <ul className="divide-y" data-testid="historial-lista">
            {convs.map(c => (
              <li key={c.thread_id} className="px-4 py-2 hover:bg-purple-50/30" data-testid={`historial-item-${c.thread_id}`}>
                <div className="flex items-start gap-2">
                  <span className="w-1 h-8 rounded flex-shrink-0 mt-0.5" style={{ background: c.account_color || '#7c3aed' }} title={c.account_label}></span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      {c.es_saliente ? (
                        <span className="text-xs font-medium text-purple-700 inline-flex items-center gap-0.5">
                          <Send className="w-3 h-3" /> Enviado
                        </span>
                      ) : (
                        <span className="text-xs font-medium text-emerald-700 inline-flex items-center gap-0.5">
                          <MailOpen className="w-3 h-3" /> Recibido
                        </span>
                      )}
                      <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: (c.account_color || '#7c3aed') + '18', color: c.account_color || '#7c3aed' }}>
                        {c.account_label}
                      </span>
                      {c.estado && c.estado !== 'nuevo' && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-700">{ESTADO_LABEL[c.estado] || c.estado}</span>
                      )}
                      {c.tiene_adjuntos && <span className="text-[10px]" title="Con adjuntos">📎</span>}
                      {c.tipo_envio === 'masivo' && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 inline-flex items-center gap-0.5" title="Envío masivo">
                          <Users className="w-2.5 h-2.5" /> Masivo
                        </span>
                      )}
                      <span className="text-[10px] text-gray-500 ml-auto">{fmtFecha(c.ultimo_mensaje_at)}</span>
                    </div>
                    <p className="text-sm text-gray-900 truncate mt-0.5">
                      {c.asunto || '(sin asunto)'}
                    </p>
                    <p className="text-xs text-gray-500 truncate">
                      {c.es_saliente ? `Para: ${c.destinatario_resumen || '(varios)'}` : `De: ${c.remitente || '(sin remitente)'}`}
                      {' · '}{c.n_mensajes || 1} mensaje{(c.n_mensajes || 1) > 1 ? 's' : ''}
                    </p>
                  </div>
                  <Link
                    to={`/admin/bandeja-unificada?thread=${encodeURIComponent(c.thread_id)}`}
                    className="p-1.5 text-gray-400 hover:text-purple-600 flex-shrink-0"
                    title="Abrir en la bandeja"
                    data-testid={`historial-open-${c.thread_id}`}
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                  </Link>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export default HistorialCorreosMatriculado;
